import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { colored, matte } from '../../Style.js';
import { SEA } from './style.js';

// Boat palette on top of the lagoon's: painted hulls, scrubbed decks, brass and iron
export const NAVY = {
  ...SEA,
  deck: 0xc89a62,
  deckDark: 0xa57a48,
  bottom: 0x8e3a2c, // Antifouling below the waterline
  brass: 0xd9a93a,
  gold: 0xe8b84a,
  coat: 0x2b2f3a,
  skin: 0xe0b08a,
  hat: 0x1f1d22,
  smoke: 0x8c8a86,
  neutralPaint: 0xe9e4d8,
  stoneLight: 0xc4bcad
};

// Shared lit material for all baked, vertex-coloured parts
export function paint({ unique = false } = {}) {
  return matte(0xffffff, { vertexColors: true, roughness: 0.75, unique });
}

let foam = null;

// White foam for bow waves and wakes, shared; per-boat size is set by scaling
export function foamMaterial() {
  if (!foam) {
    foam = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.8, depthWrite: false });
    foam.polygonOffset = true;
    foam.polygonOffsetFactor = -2;
  }
  return foam;
}

let smoke = null;

// Soft grey puffs from funnels and mortars; they fade by shrinking
export function smokeMaterial() {
  if (!smoke) {
    smoke = new THREE.MeshStandardMaterial({ color: NAVY.smoke, roughness: 1, flatShading: true, transparent: true, opacity: 0.75, depthWrite: false });
  }
  return smoke;
}

export function hexColor(color) {
  return typeof color === 'string' ? parseInt(color.replace('#', ''), 16) : color;
}

// Merge baked parts into one geometry and free the pieces.
export function bake(parts) {
  const geometry = mergeGeometries(parts);
  parts.forEach(p => p.dispose());
  return geometry;
}

// A box stretched between two points, for masts, oars and struts.
export function beam(from, to, thickness, color) {
  const a = new THREE.Vector3(...from);
  const b = new THREE.Vector3(...to);
  const dir = b.clone().sub(a);
  const geo = colored(new THREE.BoxGeometry(thickness, thickness, dir.length()), color);
  const quat = new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 0, 1), dir.normalize());
  geo.applyMatrix4(new THREE.Matrix4().compose(a.add(b).multiplyScalar(0.5), quat, new THREE.Vector3(1, 1, 1)));
  return geo;
}

// Collects coloured triangles, flipping any that face inward
class TriangleSoup {
  constructor() {
    this.positions = [];
    this.colors = [];
  }

  add(a, b, c, color, outward) {
    const ab = new THREE.Vector3().subVectors(b, a);
    const ac = new THREE.Vector3().subVectors(c, a);
    const normal = new THREE.Vector3().crossVectors(ab, ac);
    if (normal.lengthSq() < 1e-10) return;
    const centre = new THREE.Vector3().add(a).add(b).add(c).multiplyScalar(1 / 3);
    if (normal.dot(outward(centre)) < 0) [b, c] = [c, b];
    const col = new THREE.Color(color);
    for (const p of [a, b, c]) {
      this.positions.push(p.x, p.y, p.z);
      this.colors.push(col.r, col.g, col.b);
    }
  }

  quad(a, b, c, d, color, outward) {
    this.add(a, b, c, color, outward);
    this.add(a, c, d, color, outward);
  }

  geometry() {
    const geo = new THREE.BufferGeometry();
    geo.setAttribute('position', new THREE.Float32BufferAttribute(this.positions, 3));
    geo.setAttribute('color', new THREE.Float32BufferAttribute(this.colors, 3));
    geo.computeVertexNormals();
    return geo;
  }
}

// Half-width of a hull at t (0 = transom, 1 = bow), as a fraction of the beam
function hullWidth(t, transom) {
  if (t < 0.5) return transom + (1 - transom) * Math.sin((t / 0.5) * Math.PI / 2);
  return Math.max(0.03, Math.cos(((t - 0.5) / 0.5) * Math.PI / 2));
}

// A low-poly boat hull from the transom (z = stern) to a pointed bow (z = bow).
// The waterline is y = 0; the hull dips below it to `keel` where the water hides it.
// Faces are painted in bands: bottom below the waterline, `side` above it, `trim`
// along the gunwale. Includes a flat deck at `top - deckDrop`.
export function hullGeometry({
  stern, bow, beam, top, keel = -0.35, sheer = 0.2, transom = 0.75,
  side, trim, bottom = NAVY.bottom, deck = NAVY.deck, trimHeight = 0.1, deckDrop = 0.08, sections = 9
}) {
  const soup = new TriangleSoup();
  const rings = [];
  for (let i = 0; i < sections; i++) {
    const t = i / (sections - 1);
    const z = stern + (bow - stern) * t;
    const hw = (beam / 2) * hullWidth(t, transom);
    const y = top + sheer * t * t;
    const k = keel * (1 - 0.7 * t * t * t);
    // Port gunwale to starboard gunwale through the keel
    const port = [
      [-hw, y], [-hw * 0.99, y - trimHeight], [-hw * 0.9, 0.02], [-hw * 0.55, k * 0.75], [0, k]
    ];
    const ring = [...port, ...port.slice(0, 4).reverse().map(([x, py]) => [-x, py])]
      .map(([x, py]) => new THREE.Vector3(x, py, z));
    rings.push({ ring, z, hw, y });
  }

  const bandColor = k => {
    const m = Math.min(k, 7 - k);
    return m === 0 ? trim : m === 1 ? side : bottom;
  };
  const outward = c => new THREE.Vector3(c.x, c.y - (keel + top) / 2, 0);
  for (let i = 0; i < rings.length - 1; i++) {
    const r0 = rings[i].ring;
    const r1 = rings[i + 1].ring;
    for (let k = 0; k < r0.length - 1; k++) {
      soup.quad(r0[k], r1[k], r1[k + 1], r0[k + 1], bandColor(k), outward);
    }
  }

  // Transom: a fan from its centre
  const back = rings[0].ring;
  const centre = new THREE.Vector3(0, (rings[0].y + keel) / 2, rings[0].z);
  for (let k = 0; k < back.length - 1; k++) {
    soup.add(centre, back[k], back[k + 1], bandColor(k) === bottom ? bottom : side, () => new THREE.Vector3(0, 0, -1));
  }

  // Deck, slightly below the gunwale
  const up = () => new THREE.Vector3(0, 1, 0);
  for (let i = 0; i < rings.length - 1; i++) {
    const a = rings[i];
    const b = rings[i + 1];
    const da = a.y - deckDrop;
    const db = b.y - deckDrop;
    const w = 0.97;
    soup.quad(
      new THREE.Vector3(-a.hw * w, da, a.z), new THREE.Vector3(-b.hw * w, db, b.z),
      new THREE.Vector3(b.hw * w, db, b.z), new THREE.Vector3(a.hw * w, da, a.z),
      deck, up
    );
  }
  return soup.geometry();
}

// Foam at the waterline: a ring hugging the hull plus a short V spreading from
// the stern. Built at full size; scale it with speed.
export function wakeGeometry({ stern, bow, beam, transom = 0.75, spread = 0.14, tail = 0.3 }) {
  const soup = new TriangleSoup();
  const up = () => new THREE.Vector3(0, 1, 0);
  const n = 9;
  const y = 0.03;
  const outline = (grow) => {
    const pts = [];
    for (let i = 0; i < n; i++) {
      const t = i / (n - 1);
      const z = stern + (bow - stern) * t + (t === 1 ? grow * 0.3 : 0);
      pts.push([(beam / 2) * hullWidth(t, transom) + grow, z]);
    }
    return pts;
  };
  const inner = outline(0);
  const outer = outline(spread);
  for (const sx of [-1, 1]) {
    for (let i = 0; i < n - 1; i++) {
      const v = ([x, z]) => new THREE.Vector3(sx * x, y, z);
      soup.quad(v(inner[i]), v(inner[i + 1]), v(outer[i + 1]), v(outer[i]), 0xffffff, up);
    }
    // Stern wash flaring out behind the transom
    const hw = (beam / 2) * transom;
    soup.add(new THREE.Vector3(sx * hw * 0.3, y, stern), new THREE.Vector3(sx * (hw + spread), y, stern),
      new THREE.Vector3(sx * (hw * 0.6 + spread), y, stern - tail), 0xffffff, up);
  }
  soup.add(new THREE.Vector3(-(beam / 2) * transom * 0.3, y, stern), new THREE.Vector3((beam / 2) * transom * 0.3, y, stern),
    new THREE.Vector3(0, y, stern - tail * 0.8), 0xffffff, up);
  return soup.geometry();
}

// A little sailor: legs, coat, arms, head and hat, standing with feet at y = 0.
// Returns parts already merged-ready.
export function sailor({ coat, trousers = NAVY.coat, hat = null, scale = 1, x = 0, y = 0, z = 0, ry = 0, kneel = false }) {
  const s = scale;
  const parts = [];
  const legH = kneel ? 0.2 : 0.42;
  parts.push(colored(new THREE.BoxGeometry(0.3 * s, legH * s, 0.2 * s), trousers, { y: legH * s / 2 }));
  parts.push(colored(new THREE.BoxGeometry(0.38 * s, 0.5 * s, 0.26 * s), coat, { y: (legH + 0.25) * s }));
  parts.push(colored(new THREE.BoxGeometry(0.4 * s, 0.08 * s, 0.28 * s), NAVY.woodDark, { y: (legH + 0.05) * s }));
  for (const sx of [-1, 1]) {
    parts.push(colored(new THREE.BoxGeometry(0.1 * s, 0.42 * s, 0.12 * s), coat, { x: sx * 0.24 * s, y: (legH + 0.27) * s, z: 0.04 * s, rx: -0.3 }));
  }
  parts.push(colored(new THREE.BoxGeometry(0.24 * s, 0.24 * s, 0.24 * s), NAVY.skin, { y: (legH + 0.64) * s }));
  if (hat) parts.push(...hat((legH + 0.78) * s, s));
  for (const p of parts) {
    p.rotateY(ry);
    p.translate(x, y, z);
  }
  return parts;
}

// Hats for sailors, as functions of the brim height and scale
export const HATS = {
  tricorn: (y, s) => {
    const parts = [colored(new THREE.CylinderGeometry(0.16 * s, 0.18 * s, 0.12 * s, 6), NAVY.hat, { y: y + 0.06 * s })];
    for (let i = 0; i < 3; i++) {
      parts.push(colored(new THREE.BoxGeometry(0.36 * s, 0.1 * s, 0.07 * s), NAVY.hat, { z: 0.15 * s, y: y + 0.03 * s, rx: -0.5 })
        .rotateY(i * Math.PI * 2 / 3));
    }
    parts.push(colored(new THREE.BoxGeometry(0.06 * s, 0.06 * s, 0.03 * s), NAVY.gold, { y: y + 0.08 * s, z: 0.18 * s }));
    return parts;
  },
  cap: (y, s) => [
    colored(new THREE.CylinderGeometry(0.15 * s, 0.15 * s, 0.08 * s, 6), NAVY.white, { y: y + 0.03 * s }),
    colored(new THREE.BoxGeometry(0.16 * s, 0.03 * s, 0.1 * s), NAVY.coat, { y: y, z: 0.15 * s })
  ],
  helmet: (y, s) => [colored(new THREE.SphereGeometry(0.17 * s, 6, 3, 0, Math.PI * 2, 0, Math.PI / 2), NAVY.iron, { y: y - 0.04 * s })]
};

// A pennant flag on its own mesh so it can flutter, flying along +X from its hoist
export function flagGeometry(width, height) {
  const geo = new THREE.PlaneGeometry(width, height, 4, 1);
  geo.translate(width / 2, -height / 2, 0);
  return geo;
}

// Ripple a flag mesh built with flagGeometry
export function waveFlag(mesh, time, amount = 0.12) {
  const pos = mesh.geometry.attributes.position;
  const base = mesh.userData.base || (mesh.userData.base = Float32Array.from(pos.array));
  for (let i = 0; i < pos.count; i++) {
    const bx = base[i * 3];
    pos.setZ(i, Math.sin(time * 5 + bx * 4) * amount * bx);
  }
  pos.needsUpdate = true;
  mesh.geometry.computeVertexNormals();
}

// Floating motion for a boat: the mesh is the water surface it floats on, so
// the model only bobs and rolls about it, lifts the bow with speed and reports
// how fast the boat is going (0..1).
export class Buoyancy {
  constructor(mesh, model, { bob = 0.05, roll = 0.04, rate = 1.4, pitch = 0.05, topSpeed = 4 } = {}) {
    this.mesh = mesh;
    this.model = model;
    this.opts = { bob, roll, rate, pitch, topSpeed };
    this.phase = Math.random() * Math.PI * 2;
    this.speed = 0;
    this.lastTime = null;
    this.last = mesh.position.clone();
  }

  // Returns the frame time (0 on the first call)
  update() {
    const { bob, roll, rate, pitch, topSpeed } = this.opts;
    const now = performance.now() / 1000;
    const dt = this.lastTime === null ? 0 : Math.min(now - this.lastTime, 0.1);
    this.lastTime = now;
    const pos = this.mesh.position;
    if (dt > 0) {
      const moved = Math.min(Math.hypot(pos.x - this.last.x, pos.z - this.last.z), 1);
      const target = Math.min(1, moved / dt / topSpeed);
      this.speed += (target - this.speed) * Math.min(1, dt * 3);
    }
    this.last.copy(pos);

    const t = now + this.phase;
    this.model.position.y = Math.sin(t * rate) * bob;
    this.model.rotation.z = Math.sin(t * rate * 0.8 + 1) * roll;
    this.model.rotation.x = Math.sin(t * rate * 0.6) * roll * 0.5 - this.speed * pitch;
    return dt;
  }
}

// A side profile given as [z, y] points, extruded across X and centred: sails, cabins.
export function prism(points, width) {
  const shape = new THREE.Shape(points.map(([z, y]) => new THREE.Vector2(z, y)));
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: width, bevelEnabled: false });
  geometry.rotateY(-Math.PI / 2);
  geometry.translate(width / 2, 0, 0);
  return geometry;
}

// A plan outline given as [x, z] points, extruded upward from y0 to y1: barges, decks.
export function slab(points, y0, y1) {
  const shape = new THREE.Shape(points.map(([x, z]) => new THREE.Vector2(x, -z)));
  const geometry = new THREE.ExtrudeGeometry(shape, { depth: y1 - y0, bevelEnabled: false });
  geometry.rotateX(-Math.PI / 2);
  geometry.translate(0, y0, 0);
  return geometry;
}

// A ship's wheel facing +Z, spokes and rim, centred on the hub
export function shipWheel(radius, color, hub) {
  const parts = [
    colored(new THREE.TorusGeometry(radius, radius * 0.1, 3, 8), color, {}),
    colored(new THREE.CylinderGeometry(radius * 0.18, radius * 0.18, radius * 0.3, 6), hub, { rx: Math.PI / 2 })
  ];
  for (let i = 0; i < 4; i++) {
    parts.push(colored(new THREE.BoxGeometry(radius * 2.5, radius * 0.1, radius * 0.1), color, { rz: i * Math.PI / 4 }));
  }
  return parts;
}
